/**
 * Ephemeral right-click menu for decorative graphics (background images,
 * dividers, masks, waves). Opens when the iframe forwards a
 * `request-decorative-menu` postMessage, anchored at the click point.
 *
 * Decoratives are selectable. The menu keeps the covered-target escape hatch:
 * Select this graphic, Replace, and Remove all address the same TargetRef.
 *
 * The same `decorative-menu:*` events are emitted by section-context-menu.js
 * for the per-decorative entries it surfaces in the section right-click menu,
 * so a single handler module covers both trigger surfaces.
 *
 * Browse/Open Library is intentionally absent because Replace already opens
 * that backend.
 */

import { eventBus } from '../core/events.js';
import { openPopoverMenu } from './popover-menu.js';

let _initialized = false;
let _closeFn = null;
let _trigger = null;

const LABELS = {
  'decorative-divider': {
    replace: 'Replace divider…',
    remove: 'Remove decorative'
  },
  'decorative-background': {
    replace: 'Replace background image…',
    remove: 'Remove background image'
  }
};

export function initDecorativeContextMenu() {
  if (_initialized) return;
  _initialized = true;
  eventBus.on('iframe:request-decorative-menu', _onRequest);
}

export function destroyDecorativeContextMenu() {
  if (!_initialized) return;
  eventBus.off('iframe:request-decorative-menu', _onRequest);
  _initialized = false;
  _closeActiveMenu();
}

function _onRequest(payload) {
  if (!payload || !payload.instanceId) return;
  openDecorativeContextMenu(payload);
}

export function openDecorativeContextMenu(payload) {
  _closeActiveMenu();

  _trigger = _makeSyntheticTrigger(payload.point || payload.rect);
  if (!_trigger) return;

  const triggerEl = _resolveActionTrigger(_trigger);
  const items = _buildItems(payload, triggerEl);
  if (items.length === 0) {
    _closeActiveMenu();
    return;
  }

  _closeFn = openPopoverMenu(_trigger, {
    items,
    align: 'left',
    menuLabel:
      payload.targetKind === 'decorative-background'
        ? 'Background image actions'
        : 'Decorative actions',
    consumeCanvasDismiss: true,
    onClose: () => {
      _closeFn = null;
      if (_trigger && _trigger.parentNode) _trigger.parentNode.removeChild(_trigger);
      _trigger = null;
    }
  });
}

function _buildItems(payload, triggerEl = null) {
  const labels = LABELS[payload.targetKind];
  if (!labels) return [];
  const capabilities = Array.isArray(payload.capabilities) ? payload.capabilities : [];

  const items = [
    {
      id: 'select',
      label: 'Select this graphic',
      onSelect: () => eventBus.emit('decorative-menu:select', { target: payload, triggerEl })
    }
  ];
  for (const capability of capabilities.filter((value) => value !== 'open-library')) {
    const label = labels[capability];
    if (!label) continue;
    items.push({
      id: capability,
      label,
      danger: capability === 'remove',
      onSelect: () => {
        eventBus.emit(`decorative-menu:${capability}`, { target: payload, triggerEl });
      }
    });
  }
  return items;
}

function _resolveActionTrigger(fallback) {
  return document.getElementById('pb-preview') || fallback || null;
}

function _closeActiveMenu() {
  if (typeof _closeFn === 'function') {
    try {
      _closeFn();
    } catch {
      /* noop */
    }
  }
  _closeFn = null;
  if (_trigger && _trigger.parentNode) {
    _trigger.parentNode.removeChild(_trigger);
  }
  _trigger = null;
}

function _makeSyntheticTrigger(rect) {
  const iframe = document.getElementById('pb-preview');
  if (!iframe) return null;
  const iframeRect = iframe.getBoundingClientRect();

  const r = rect || { top: 0, left: 0, width: 0, height: 0 };
  const left = iframeRect.left + Number(r.left || 0);
  const top = iframeRect.top + Number(r.top || 0);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-hidden', 'true');
  btn.tabIndex = -1;
  btn.style.position = 'fixed';
  btn.style.left = `${left}px`;
  btn.style.top = `${top}px`;
  btn.style.width = '1px';
  btn.style.height = '1px';
  btn.style.padding = '0';
  btn.style.margin = '0';
  btn.style.border = '0';
  btn.style.background = 'transparent';
  btn.style.opacity = '0';
  btn.style.pointerEvents = 'none';
  document.body.appendChild(btn);
  return btn;
}
